import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useMutation } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { SignUpUrl, post, queryKeys } from '@mobi/account/Utils'
import type { AttemptLoginReturn, LogonRequestData } from '@mobi/api-types'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors, font } from '@mobi/component-library/Foundations'
import { Password } from '@mobi/component-library/Selection&Input/Inputs'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import {
  getSignUpState,
  setIsNewUserAutoLoggedIn,
  setIsWageringAccountPendingCreation,
} from '../Store'
import { routes } from '../Utils'
import { PasswordCriteriaList } from './PasswordCriteriaList'
import { PasswordCriteria, validatePassword } from './PasswordValidate'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { SignupError } from './SignupError'

type PropPassword = {
  onCompleted?: (password: string) => void
  attemptLogin: (data: LogonRequestData) => Promise<AttemptLoginReturn>
}

type TypeCreateBetAccountResponse = {
  betAccountNumber: number | null
}

type TypeCreateBetAccountRequest = {
  applicationReferenceNumber: number
  password: string
  enquiryId: string
}

export const PasswordConfirm = ({ onCompleted, attemptLogin }: PropPassword) => {
  const [password, setPassword] = useState('')
  const [passwordUnmetCriteria, setPasswordUnmetCriteria] = useState<PasswordCriteria[]>([
    PasswordCriteria.containsNumberOrSpecialCharacter,
    PasswordCriteria.containsUpperAndLowerLetters,
    PasswordCriteria.sufficientLength,
  ])
  const [confirmationPassword, setConfirmationPassword] = useState('')
  const [showErrorPopup, setShowErrorPopup] = useState(false)
  const signUpState = useSelector(getSignUpState)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isPasswordValid = passwordUnmetCriteria.length === 0
  const [confirmationAttempted, setConfirmationAttempted] = useState(false)
  if (
    !confirmationAttempted &&
    !!confirmationPassword &&
    confirmationPassword.length >= password.length
  ) {
    setConfirmationAttempted(true)
  }

  const canContinue = isPasswordValid && password === confirmationPassword
  const confirmationPasswordError =
    confirmationAttempted && password !== confirmationPassword ? 'The passwords do not match.' : ''

  const createWageringAccount = useMutation<
    TypeCreateBetAccountResponse,
    Error,
    TypeCreateBetAccountRequest
  >({
    mutationFn: ({
      applicationReferenceNumber,
      password,
      enquiryId,
    }: TypeCreateBetAccountRequest) => {
      return post(SignUpUrl.createBetAccount, {
        applicationReferenceNumber,
        password,
        enquiryId,
      })
    },
    mutationKey: queryKeys.signupCreateWageringBetAccount,
    onSuccess: async (data, { password }) => {
      const { betAccountNumber } = data

      if (betAccountNumber === null) {
        // user tries to create wagering account on an IBAA application that is pending.
        // application is stuck so suggest them to call CMC
        dispatch(setIsWageringAccountPendingCreation(true))
        navigate(routes.signUpUnsuccessful)
        return
      }

      const accountIdentifier = betAccountNumber.toString()

      signupTracker({
        interaction_type: 'Sign Up Complete',
        signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
      })

      addToast({
        id: 'account-creation-success',
        icon: 'success',
        theme: 'dark',
        title: 'Your account has been successfully created',
      })

      const newDeviceSecret = uuidv4()

      const result = await attemptLogin({
        accountIdentifier,
        password,
        keepLoggedIn: false,
        rememberAccount: false,
        deviceSecret: newDeviceSecret,
      })

      if (result.status === 'FAILED' || result.errorMessage) {
        dispatch(setIsNewUserAutoLoggedIn(false))
        navigate(routes.signUpUnsuccessful)
        signupTracker({
          interaction_type: 'Login Failed',
          location: 'Sign Up',
          signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
        })
        return
      }

      if (result.status === 'SUCCESSFUL') {
        onCompleted?.(password)

        dispatch(setIsNewUserAutoLoggedIn(true))
        navigate(routes.phoneNumberVerification)
      } else {
        throw new Error('Unable to login with created bet account')
      }
    },
    onError: error => {
      signupTracker({
        interaction_type: 'Sign Up Error',
        signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: unwrapErrorMessage(error),
      })
      setShowErrorPopup(true)
    },
  })

  return (
    <>
      <SignUpPageStyled>
        <StepGroup currentStep={4} totalSteps={5} />
        <AccountHeader
          alignItems='start'
          title='Create your password'
          description='Create a password to keep your account secured.'
          style={{ marginBottom: '0rem' }}
        />
        <Content>
          <PasswordInputStyled>
            <Password
              name='password'
              label='Password'
              value={password}
              onInput={e => {
                const newValue = e.currentTarget.value
                setPassword(newValue)

                const { unmetCriteria } = validatePassword(newValue)
                setPasswordUnmetCriteria(unmetCriteria)
              }}
              isDisabled={createWageringAccount.isPending}
              isInvalid={passwordUnmetCriteria.includes(PasswordCriteria.containsComma)}
              helpText={
                passwordUnmetCriteria.includes(PasswordCriteria.containsComma)
                  ? 'Commas are not valid. Please use a different character.'
                  : ''
              }
            />

            <PasswordCriteriaList unmetCriteria={passwordUnmetCriteria} />

            <Password
              name='confirm-password'
              label='Confirm Password'
              isInvalid={!!confirmationPasswordError}
              helpText={confirmationPasswordError}
              value={confirmationPassword}
              onInput={e => setConfirmationPassword(e.currentTarget.value)}
              isDisabled={createWageringAccount.isPending}
            />
          </PasswordInputStyled>
        </Content>
        <Footer>
          <Button
            aria-label='Register button'
            color='primary'
            isFullWidth
            size='md'
            disabled={!canContinue}
            isLoading={createWageringAccount.isPending}
            onClick={() => {
              createWageringAccount.mutate({
                applicationReferenceNumber:
                  signUpState.signupApplication.applicationReferenceNumber,
                password: password,
                enquiryId: signUpState.signupApplication.enquiryId,
              })
            }}
          >
            Register
          </Button>
          <HelperText>Complete all fields before continuing</HelperText>
        </Footer>
      </SignUpPageStyled>
      <SignupError
        isOpen={showErrorPopup}
        applicationNumber={signUpState.signupApplication.applicationReferenceNumber}
        onClose={() => {
          createWageringAccount.reset()
          setShowErrorPopup(false)
        }}
      />
    </>
  )
}

export const HelperText = styled.div({
  fontFamily: font.family.primary,
  color: colors.neutral[600],
  ...font.size.md,
})

export const PasswordInputStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
})
